import React, { useEffect, useState } from "react";
import { resolveDrawingUrl } from '../../utils/urlHelper';

const toSvgPoint = (point) => `${point.x * 1000},${point.y * 1000}`;

const parsePoints = (value) => {
  if (!value) return [];
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function SitePolygonEditor({
  layout,
  value,
  onChange,
  fillColor = "#f97316",
  onFillColorChange,
  zoneId,
}) {
  const [points, setPoints] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    setPoints(parsePoints(value));
  }, [value]);

  const drawingUrl = resolveDrawingUrl(layout?.site?.Drawing_url);
  const drawingType = layout?.site?.Drawing_type;

  const commitPoints = (nextPoints) => {
    setPoints(nextPoints);
    onChange(nextPoints.length >= 3 ? JSON.stringify(nextPoints) : null);
  };

  const getNormalizedPoint = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
  };

  const handleCanvasClick = (event) => {
    if (dragIndex !== null) return;
    commitPoints([...points, getNormalizedPoint(event)]);
  };

  const handlePointerMove = (event) => {
    if (dragIndex === null) return;
    const nextPoints = [...points];
    nextPoints[dragIndex] = getNormalizedPoint(event);
    commitPoints(nextPoints);
  };

  const existingZones = (layout?.zones || []).filter(
    (zone) => zone.Zone_ID !== zoneId && zone.polygon?.length >= 3
  );

  if (!layout?.site) return null;

  if (!drawingUrl) {
    return (
      <div className="col-span-full rounded-xl border border-dashed border-gray-300 p-4 text-sm text-gray-500">
        No site drawing has been uploaded for the selected site.
      </div>
    );
  }

  return (
    <div className="col-span-full flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[#4C4C66]">Zone polygon</p>
          <p className="text-xs text-gray-500">Click the drawing to add points. Drag points to adjust the shape.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={fillColor}
            onChange={(event) => onFillColorChange?.(event.target.value)}
            className="h-9 w-10 cursor-pointer rounded border"
            title="Zone fill color"
          />
          <button
            type="button"
            onClick={() => commitPoints(points.slice(0, -1))}
            className="rounded-lg border px-3 py-2 text-xs font-semibold text-[#4C4C66]"
          >
            Undo
          </button>
          <button
            type="button"
            onClick={() => commitPoints([])}
            className="rounded-lg border px-3 py-2 text-xs font-semibold text-[#4C4C66]"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="flex justify-center overflow-hidden rounded-xl border bg-gray-50 p-3">
        {drawingType === "pdf" ? (
          <object data={drawingUrl} type="application/pdf" className="h-[520px] w-full">
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              PDF preview is unavailable in this browser.
            </div>
          </object>
        ) : (
          <div className="relative max-w-full" style={{ width: "fit-content" }}>
            <img src={drawingUrl} alt="Site drawing" className="block max-h-[520px] max-w-full" />

            <svg
              viewBox="0 0 1000 1000"
              preserveAspectRatio="none"
              className="absolute inset-0 h-full w-full cursor-crosshair"
              onClick={handleCanvasClick}
              onPointerMove={handlePointerMove}
              onPointerUp={() => setDragIndex(null)}
              onPointerLeave={() => setDragIndex(null)}
            >
              {existingZones.map((zone) => (
                <polygon
                  key={zone.Zone_ID}
                  points={zone.polygon.map(toSvgPoint).join(" ")}
                  fill={zone.Fill_color || "#64748b"}
                  fillOpacity="0.18"
                  stroke={zone.Fill_color || "#64748b"}
                  strokeWidth="4"
                  pointerEvents="none"
                />
              ))}

              {points.length >= 2 && (
                <polyline
                  points={points.map(toSvgPoint).join(" ")}
                  fill="none"
                  stroke={fillColor}
                  strokeWidth="6"
                  pointerEvents="none"
                />
              )}
              {points.length >= 3 && (
                <polygon
                  points={points.map(toSvgPoint).join(" ")}
                  fill={fillColor}
                  fillOpacity="0.28"
                  stroke={fillColor}
                  strokeWidth="6"
                  pointerEvents="none"
                />
              )}
              {points.map((point, index) => (
                <circle
                  key={`${point.x}-${point.y}-${index}`}
                  cx={point.x * 1000}
                  cy={point.y * 1000}
                  r="14"
                  fill="#ffffff"
                  stroke={fillColor}
                  strokeWidth="6"
                  className="cursor-move"
                  onClick={(event) => event.stopPropagation()}
                  onPointerDown={(event) => {
                    event.stopPropagation();
                    setDragIndex(index);
                  }}
                />
              ))}
            </svg>
          </div>
        )}
      </div>

      {points.length > 0 && points.length < 3 && (
        <p className="text-xs text-red-500">Add at least 3 points to save a zone polygon.</p>
      )}
    </div>
  );
}
